import { Box, Stack, Group, Text, Badge, ThemeIcon, UnstyledButton } from "@mantine/core";
import { IconBriefcase, IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { CandidateAvatar } from "../Candidates/CandidateAvatar";
import CandidateHoverCard from "../components/CandidateHoverCard";
import LanguageBadges from "../components/LanguageBadges";
import { formatCandidateName } from "../_Shared/candidateName";
import { candidateRoleAndCompany } from "../_Shared/candidateRole";
import CompanyLink from "../components/CompanyLink";
import { useStore } from "../store";

const rowStyle = {
  border: "1px solid var(--mantine-color-default-border)",
  borderRadius: 8,
  padding: 8,
};

// Renders the entities the assistant referenced (the candidates/projects its tool calls touched)
// as compact, clickable cards beneath its answer. Reuses the same avatar + hover-card + language
// chips as the rest of the app, so a referenced candidate looks and behaves identically here and
// in the contact list. Data is resolved server-side — no fetch here. Clicking a card routes to the
// profile (the parent closes the drawer first).
export default function AssistantReferences({ references, moreCandidates = 0, moreProjects = 0, onNavigate }) {
  const { t } = useTranslation("Veft");
  const nameFormat = useStore((s) => s.nameFormat);

  const candidates = (references ?? []).filter((r) => r.kind === "candidate" && r.candidate);
  const projects = (references ?? []).filter((r) => r.kind === "project" && r.project);
  if (candidates.length === 0 && projects.length === 0) return null;

  return (
    <Box mt="sm">
      <Text size="xs" c="dimmed" fw={500} mb={6}>{t("assistant_referenced")}</Text>
      <Stack gap={6}>
        {candidates.map((r) => {
          const c = r.candidate;
          const { role, company, companyId, isFormer } = candidateRoleAndCompany(c);
          const roleLabel = isFormer && role ? `${t("role_former_prefix")} ${role}` : role;
          return (
            <UnstyledButton key={`c-${r.id}`} w="100%" onClick={() => onNavigate?.(`/candidates/${r.id}`)}>
              <Group gap="sm" wrap="nowrap" style={rowStyle}>
                <CandidateHoverCard candidate={c}>
                  <CandidateAvatar candidate={c} size="sm" radius="xl" color="blue" style={{ flexShrink: 0 }} />
                </CandidateHoverCard>
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm" fw={500} truncate>{formatCandidateName(c, nameFormat)}</Text>
                    {c.openToWork && <Badge size="xs" color="teal" variant="light">{t("open_to_work")}</Badge>}
                  </Group>
                  {(role || company) && (
                    <Text size="xs" c="dimmed" truncate>
                      {roleLabel}{roleLabel && company ? " · " : ""}
                      <CompanyLink company={company} companyId={companyId} />
                    </Text>
                  )}
                  {(c.languages ?? []).length > 0 && (
                    <Box mt={2}><LanguageBadges languages={c.languages} max={4} /></Box>
                  )}
                </Box>
                <IconChevronRight size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
              </Group>
            </UnstyledButton>
          );
        })}
        {moreCandidates > 0 && (
          <Text size="xs" c="dimmed" pl={6}>{t("assistant_more", { count: moreCandidates })}</Text>
        )}

        {projects.map((r) => {
          const p = r.project;
          const sub = [p.roleTitle, p.clientName].filter(Boolean).join(" · ");
          return (
            <UnstyledButton key={`p-${r.id}`} w="100%" onClick={() => onNavigate?.(`/projects/${r.id}`)}>
              <Group gap="sm" wrap="nowrap" style={rowStyle}>
                <ThemeIcon variant="light" color="blue" size="md" radius="xl" style={{ flexShrink: 0 }}>
                  <IconBriefcase size={16} />
                </ThemeIcon>
                <Box style={{ minWidth: 0, flex: 1 }}>
                  <Text size="sm" fw={500} truncate>{p.title}</Text>
                  {(sub || p.candidateCount > 0) && (
                    <Text size="xs" c="dimmed" truncate>
                      {sub}
                      {p.candidateCount > 0 ? `${sub ? " · " : ""}${t("assistant_candidate_count", { count: p.candidateCount })}` : ""}
                    </Text>
                  )}
                </Box>
                <IconChevronRight size={14} style={{ flexShrink: 0, opacity: 0.5 }} />
              </Group>
            </UnstyledButton>
          );
        })}
        {moreProjects > 0 && (
          <Text size="xs" c="dimmed" pl={6}>{t("assistant_more", { count: moreProjects })}</Text>
        )}
      </Stack>
    </Box>
  );
}
